import { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import {
  Box,
  Dialog,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { normalizeString } from "helpers/normalizeString";
import type { IAtributo, TipoAtributo } from "interfaces/loja";
import {
  fieldSx,
  modalHeaderSx,
  modalPaperSx,
  saveButtonSx,
  tipoToggleSx,
} from "../atributos.theme";
import {
  TIPO_DESCRICOES,
  TIPO_LABELS,
  TIPO_OPCOES,
} from "./atributos.helpers";

type Props = {
  open: boolean;
  editing: IAtributo | null;
  saving: boolean;
  onClose: () => void;
  onSave: (payload: { nome: string; tipo: TipoAtributo }) => void;
};

export default function AtributoFormDialog({
  open,
  editing,
  saving,
  onClose,
  onSave,
}: Props) {
  const [nome, setNome] = useState("");
  const [tipo, setTipo] = useState<TipoAtributo | "">("");

  useEffect(() => {
    if (!open) return;
    setNome(editing?.nome ?? "");
    setTipo(editing?.tipo ?? "");
  }, [open, editing]);

  const slugPreview = nome.trim() ? normalizeString(nome) : "";

  const handleSave = () => {
    if (!nome.trim() || !tipo) return;
    onSave({ nome: nome.trim(), tipo });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { ...modalPaperSx, maxWidth: 560 } }}
    >
      <Box sx={modalHeaderSx}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center" minWidth={0}>
            <CategoryOutlinedIcon sx={{ fontSize: 36, opacity: 0.9 }} />
            <Box minWidth={0}>
              <Typography variant="overline" sx={{ opacity: 0.75, letterSpacing: 1.2 }}>
                Catálogo da loja
              </Typography>
              <Typography variant="h5" fontWeight="bold" noWrap>
                {editing ? "Editar atributo" : "Novo atributo"}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} sx={{ color: "inherit" }} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <MDBox sx={{ p: 3 }}>
        <Stack spacing={2.5}>
          <TextField
            label="Nome do atributo"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Ex.: Tamanho, Cor, Material…"
            fullWidth
            size="small"
            sx={fieldSx}
            autoFocus
          />
          {slugPreview ? (
            <MDTypography variant="caption" color="text.secondary">
              Slug gerado: <strong>{slugPreview}</strong>
            </MDTypography>
          ) : null}

          <Stack spacing={0.75}>
            <MDTypography variant="caption" color="text.secondary" fontWeight="bold">
              Tipo de dado
            </MDTypography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={tipo}
              onChange={(_, next: TipoAtributo | "" | null) => {
                if (next === null) return;
                setTipo(next);
              }}
              sx={tipoToggleSx}
            >
              {TIPO_OPCOES.map((t) => (
                <ToggleButton key={t} value={t}>
                  {TIPO_LABELS[t]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {tipo ? (
              <MDTypography variant="caption" color="text.secondary">
                {TIPO_DESCRICOES[tipo]}
              </MDTypography>
            ) : null}
          </Stack>
        </Stack>
      </MDBox>

      <MDBox
        px={3}
        py={2}
        display="flex"
        justifyContent="flex-end"
        gap={1}
        borderTop="1px solid"
        borderColor="grey.200"
      >
        <MDButton variant="outlined" color="dark" onClick={onClose}>
          Cancelar
        </MDButton>
        <MDButton
          variant="gradient"
          color="dark"
          onClick={handleSave}
          disabled={saving || !nome.trim() || !tipo}
          sx={saveButtonSx}
        >
          {saving ? "Salvando…" : "Salvar atributo"}
        </MDButton>
      </MDBox>
    </Dialog>
  );
}
